(function terrainoBuildsCompendiumModule(global) {
  'use strict';

  var PUBLIC_ENDPOINT = '/apps/terraino-hub/v1/public/builds';
  var ALLOWED_KEYS = ['query', 'theme', 'difficulty', 'bundle', 'sort', 'page'];

  function normalizeState(input) {
    var source = input || {};
    var page = Number.parseInt(source.page, 10);
    return {
      query: String(source.query || '').trim().slice(0, 120),
      theme: String(source.theme || '').trim(),
      difficulty: String(source.difficulty || '').trim(),
      bundle: String(source.bundle || '').trim(),
      sort: String(source.sort || 'latest').trim() || 'latest',
      page: Number.isInteger(page) && page > 0 ? page : 1,
    };
  }

  function buildQuery(state) {
    var normalized = normalizeState(state);
    var params = new URLSearchParams();
    ALLOWED_KEYS.forEach(function appendAllowedKey(key) {
      var value = normalized[key];
      if ((key === 'page' && value === 1) || value === '') return;
      params.set(key, String(value));
    });
    return params;
  }

  function buildRequestUrl(state) {
    var query = buildQuery(state).toString();
    return PUBLIC_ENDPOINT + (query ? '?' + query : '');
  }

  function toHistoryQuery(state) {
    var query = buildQuery(state).toString();
    return query ? '?' + query : '';
  }

  function createLoader(options) {
    var fetchImpl = options.fetchImpl;
    var onSuccess = options.onSuccess;
    var onError = options.onError;
    var urlForState = options.urlForState || buildRequestUrl;
    var controller = null;
    var generation = 0;

    return {
      load: async function load(state) {
        generation += 1;
        var requestGeneration = generation;
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          var response = await fetchImpl(urlForState(state), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          });
          if (!response.ok) throw new Error('Builds request failed with status ' + response.status);
          var payload = await response.json();
          if (!payload || payload.schemaVersion !== 1 || !Array.isArray(payload.builds)) {
            throw new Error('Builds response did not match schema version 1');
          }
          if (requestGeneration === generation) onSuccess(payload);
        } catch (error) {
          if (requestGeneration !== generation) return;
          if (error && error.name === 'AbortError') return;
          onError(error);
        }
      },
      abort: function abort() {
        generation += 1;
        if (controller) controller.abort();
      },
    };
  }

  function debounce(callback, delay) {
    var timer = null;
    return function debounced() {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function invoke() { callback.apply(null, args); }, delay);
    };
  }

  function addText(element, value) {
    element.textContent = value == null ? '' : String(value);
    return element;
  }

  function appendMeta(documentObject, container, build) {
    var meta = documentObject.createElement('p');
    meta.className = 'terraino-hub__kicker';
    addText(meta, [build.theme, build.difficulty].filter(Boolean).join(' · '));
    container.appendChild(meta);
  }

  function buildHref(build) {
    var slug = String(build.slug || '');
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return '/pages/builds-compendium';
    return '/pages/builds/' + encodeURIComponent(slug);
  }

  function normalizeHeroImage(heroImage) {
    var source = typeof heroImage === 'string' ? heroImage : heroImage && heroImage.url;
    if (typeof source !== 'string' || (!/^https:\/\//.test(source) && !/^\/(?!\/)/.test(source))) return null;
    return {
      src: source,
      altText: typeof heroImage === 'object' && typeof heroImage.altText === 'string' ? heroImage.altText : '',
      width: typeof heroImage === 'object' && Number.isSafeInteger(heroImage.width) && heroImage.width > 0 ? heroImage.width : null,
      height: typeof heroImage === 'object' && Number.isSafeInteger(heroImage.height) && heroImage.height > 0 ? heroImage.height : null,
    };
  }

  function renderBuildCards(documentObject, container, builds) {
    container.replaceChildren();
    builds.forEach(function renderBuild(build) {
      var article = documentObject.createElement('article');
      article.className = 'terraino-hub__build-card';
      var href = buildHref(build);

      var mediaLink = documentObject.createElement('a');
      mediaLink.className = 'terraino-hub__build-image';
      mediaLink.href = href;
      mediaLink.setAttribute('aria-label', 'Open ' + String(build.title || 'Build'));
      var hero = normalizeHeroImage(build.heroImage);
      if (hero) {
        var image = documentObject.createElement('img');
        image.src = hero.src;
        image.alt = hero.altText;
        if (hero.width) image.width = hero.width;
        if (hero.height) image.height = hero.height;
        image.loading = 'lazy';
        mediaLink.appendChild(image);
      } else {
        var placeholder = documentObject.createElement('span');
        placeholder.setAttribute('aria-hidden', 'true');
        addText(placeholder, 'TERRAINO');
        mediaLink.appendChild(placeholder);
      }
      article.appendChild(mediaLink);

      var body = documentObject.createElement('div');
      body.className = 'terraino-hub__build-body';
      appendMeta(documentObject, body, build);
      var heading = documentObject.createElement('h2');
      var link = documentObject.createElement('a');
      link.href = href;
      addText(link, build.title || 'Untitled build');
      heading.appendChild(link);
      body.appendChild(heading);
      if (build.builderName) {
        var builder = documentObject.createElement('p');
        addText(builder, 'Built by ' + build.builderName);
        body.appendChild(builder);
      }
      var bundles = documentObject.createElement('ul');
      bundles.className = 'terraino-hub__bundle-labels';
      bundles.setAttribute('aria-label', 'Required TERRAINO systems');
      (build.requiredBundleCategories || []).forEach(function appendBundle(code) {
        var item = documentObject.createElement('li');
        addText(item, String(code).replaceAll('_', ' '));
        bundles.appendChild(item);
      });
      body.appendChild(bundles);
      article.appendChild(body);
      container.appendChild(article);
    });
  }

  function stateFromForm(form) {
    var formData = new FormData(form);
    return normalizeState(Object.fromEntries(formData.entries()));
  }

  function buildProtectedPieceRequestUrl(state, protectedApi, cryptoObject, origin) {
    var normalized = normalizeState(state);
    var piece = String(state && state.piece || '');
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(piece)) {
      throw new Error('Protected Piece filter requires a valid slug');
    }
    var params = buildQuery(normalized);
    params.set('piece', piece);
    var endpoint = '/apps/terraino-hub/v1/protected/builds?' + params.toString();
    return protectedApi.buildProtectedUrl(endpoint, 'v1/protected/builds', cryptoObject, origin);
  }

  function toAuthorizedHistoryQuery(state) {
    var params = buildQuery(state);
    if (state && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(state.piece || ''))) {
      params.set('piece', state.piece);
    }
    var query = params.toString();
    return query ? '?' + query : '';
  }

  function renderPagination(documentObject, container, payload, state, onPage) {
    if (!Number.isSafeInteger(payload.page) || payload.page < 1
      || !Number.isSafeInteger(payload.totalPages) || payload.totalPages < 0) {
      throw new Error('Builds response contained invalid pagination');
    }
    container.replaceChildren();
    container.hidden = payload.totalPages <= 1;
    if (container.hidden) return;

    function appendPage(label, page) {
      var link = documentObject.createElement('a');
      link.href = '/pages/builds-compendium' + toAuthorizedHistoryQuery(Object.assign({}, state, { page: page }));
      addText(link, label);
      link.addEventListener('click', function navigatePage(event) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        onPage(page);
      });
      container.appendChild(link);
    }
    if (payload.page > 1) appendPage('Previous', payload.page - 1);
    container.appendChild(addText(documentObject.createElement('span'), 'Page ' + payload.page + ' of ' + payload.totalPages));
    if (payload.page < payload.totalPages) appendPage('Next', payload.page + 1);
  }

  async function fetchBuildFilterOptions(fetchImpl) {
    var facets = { theme: new Set(), difficulty: new Set() };
    var slugs = new Set();
    var page = 1;
    var totalPages;
    var totalItems;
    do {
      var response = await fetchImpl(buildRequestUrl({ page: page, sort: 'title' }), {
        credentials: 'same-origin', headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Build filter options request failed');
      var payload = await response.json();
      if (!payload || payload.schemaVersion !== 1 || !Array.isArray(payload.builds)
        || payload.page !== page || !Number.isSafeInteger(payload.totalPages)
        || payload.totalPages < 0 || payload.totalPages > 250
        || !Number.isSafeInteger(payload.totalItems) || payload.totalItems < 0
        || (totalPages != null && (payload.totalPages !== totalPages || payload.totalItems !== totalItems))) {
        throw new Error('Build filter options catalog was incomplete');
      }
      totalPages = payload.totalPages;
      totalItems = payload.totalItems;
      payload.builds.forEach(function collectOptions(build) {
        if (!build || typeof build.slug !== 'string' || !build.slug || slugs.has(build.slug)) {
          throw new Error('Build filter options catalog changed while loading');
        }
        slugs.add(build.slug);
        Object.keys(facets).forEach(function collectField(name) {
          var value = typeof build[name] === 'string' ? build[name].trim() : '';
          if (value) facets[name].add(value);
        });
      });
      page += 1;
    } while (page <= totalPages);
    if (slugs.size !== totalItems) throw new Error('Build filter options catalog was incomplete');
    return { theme: Array.from(facets.theme).sort(), difficulty: Array.from(facets.difficulty).sort() };
  }

  function ensureFilterOption(documentObject, select, value) {
    if (!value || !select.options || Array.from(select.options).some(function matches(option) { return option.value === value; })) return;
    var option = documentObject.createElement('option');
    option.value = value;
    addText(option, value);
    select.appendChild(option);
  }

  function renderFilterOptions(documentObject, select, values) {
    var selected = select.value;
    var all = select.options[0];
    select.replaceChildren(all);
    values.forEach(function appendOption(value) { ensureFilterOption(documentObject, select, value); });
    ensureFilterOption(documentObject, select, selected);
    select.value = selected;
  }

  async function fetchAllPieceFacetPages(options) {
    var pieces = [];
    var page = 1;
    var totalPages = 1;
    do {
      var endpoint = page === 1
        ? options.endpoint
        : options.endpoint + (options.endpoint.includes('?') ? '&' : '?') + 'page=' + page;
      var payload = await options.protectedApi.fetchProtected({
        endpoint: endpoint,
        resourcePath: options.resourcePath,
        fetchImpl: options.fetchImpl,
        cryptoObject: options.cryptoObject,
        origin: options.origin,
      });
      if (!payload || payload.schemaVersion !== 1 || !Array.isArray(payload.pieces)
        || !Number.isSafeInteger(payload.page) || payload.page !== page
        || !Number.isSafeInteger(payload.totalPages) || payload.totalPages < 0 || payload.totalPages > 250) {
        throw new Error('Protected Piece facet response was incomplete');
      }
      pieces.push.apply(pieces, payload.pieces);
      totalPages = payload.totalPages;
      page += 1;
    } while (page <= totalPages);
    return pieces;
  }

  function buildPieceFacetOptions(pieces) {
    var names = new Map();
    var options = pieces.filter(function validPiece(piece) {
      return piece && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(piece.slug || ''));
    }).map(function baseOption(piece) {
      var name = String(piece.name || piece.slug).trim();
      var key = name.toLowerCase();
      names.set(key, (names.get(key) || 0) + 1);
      return { value: piece.slug, label: name, nameKey: key, piece: piece };
    });
    var labels = new Map();
    options.forEach(function describeDuplicate(option) {
      if (names.get(option.nameKey) > 1) {
        var details = [option.piece.pieceKitProxyName, option.piece.pieceSubtypeName].filter(Boolean);
        option.label += ' — ' + (details.length ? details.join(' · ') : option.value);
      }
      var key = option.label.toLowerCase();
      labels.set(key, (labels.get(key) || 0) + 1);
    });
    return options.map(function distinctOption(option) {
      if (labels.get(option.label.toLowerCase()) > 1) {
        option.label = String(option.piece.name || option.value).trim() + ' — ' + option.value;
      }
      return { value: option.value, label: option.label };
    });
  }

  async function initializePieceFacet(mount, form, isCurrent) {
    var protectedApi = global.TerrainoProtectedData;
    if (!protectedApi || !mount.dataset.terrainoEndpoint || !mount.dataset.terrainoResourcePath) return null;
    try {
      var pieces = await fetchAllPieceFacetPages({
        protectedApi: protectedApi,
        endpoint: mount.dataset.terrainoEndpoint,
        resourcePath: mount.dataset.terrainoResourcePath,
        fetchImpl: global.fetch.bind(global),
        cryptoObject: global.crypto,
        origin: global.location.origin,
      });
      if (isCurrent && !isCurrent()) return null;
      var label = global.document.createElement('label');
      label.setAttribute('for', 'TerrainoBuildPieceFacet');
      addText(label, 'Piece');
      var select = global.document.createElement('select');
      select.id = 'TerrainoBuildPieceFacet';
      select.name = 'piece';
      var all = global.document.createElement('option');
      all.value = '';
      addText(all, 'All pieces');
      select.appendChild(all);
      buildPieceFacetOptions(pieces).forEach(function appendPiece(piece) {
        var option = global.document.createElement('option');
        option.value = piece.value;
        addText(option, piece.label);
        select.appendChild(option);
      });
      mount.replaceChildren(label, select);
      var requested = new URLSearchParams(global.location.search).get('piece');
      if (requested && Array.from(select.options).some(function hasOption(option) { return option.value === requested; })) {
        select.value = requested;
      }
      var description = form.querySelector('[data-terraino-piece-selection]');
      if (!description) {
        description = global.document.createElement('p');
        description.id = select.id + '-description';
        description.className = 'terraino-hub__filter-selection';
        description.setAttribute('data-terraino-piece-selection', '');
        form.appendChild(description);
      }
      select.setAttribute('aria-describedby', description.id);
      function describeSelection() {
        var selected = select.options[select.selectedIndex];
        addText(description, select.value && selected ? 'Selected Piece: ' + selected.textContent : '');
      }
      select.addEventListener('change', describeSelection);
      describeSelection();
      return select;
    } catch (_error) {
      if (!isCurrent || isCurrent()) mount.replaceChildren();
      return null;
    }
  }

  var initializedRoots = new WeakSet();

  function initialize(root) {
    if (initializedRoots.has(root)) return;
    var form = root.querySelector('[data-terraino-build-filters]');
    var results = root.querySelector('[data-terraino-build-results]');
    var status = root.querySelector('[data-terraino-build-status]');
    if (!form || !results || !status) return;
    initializedRoots.add(root);
    results.setAttribute('role', 'region');
    results.setAttribute('tabindex', '-1');
    results.setAttribute('aria-label', 'Build results');

    var pagination = root.querySelector('[data-terraino-build-pagination]');
    if (!pagination) {
      pagination = global.document.createElement('nav');
      pagination.className = 'terraino-hub__pagination';
      pagination.setAttribute('aria-label', 'Build pages');
      pagination.setAttribute('data-terraino-build-pagination', '');
      pagination.hidden = true;
      root.appendChild(pagination);
    }
    pagination.hidden = true;

    var initialParams = new URLSearchParams(global.location.search);
    var initial = normalizeState(Object.fromEntries(initialParams.entries()));
    var requestedPiece = initialParams.get('piece') || '';
    var waitingForRequestedPiece = Boolean(requestedPiece);
    var pieceFacetLoading = false;
    var pieceFacetGeneration = 0;
    ALLOWED_KEYS.forEach(function restoreControl(name) {
      var control = form.elements.namedItem(name);
      if (control && (name === 'theme' || name === 'difficulty')) {
        ensureFilterOption(global.document, control, initial[name]);
      }
      if (control && initial[name] != null) control.value = String(initial[name]);
    });
    var currentState = initial;
    var currentPiece = '';
    var focusResultsOnLoad = false;

    var optionsStatus = root.querySelector('[data-terraino-build-options-status]');
    var optionsLoading = false;
    async function loadFilterOptions() {
      if (optionsLoading) return;
      optionsLoading = true;
      addText(optionsStatus, 'Loading all filter options…');
      try {
        var facets = await fetchBuildFilterOptions(global.fetch.bind(global));
        ['theme', 'difficulty'].forEach(function updateOptions(name) {
          var select = form.elements.namedItem(name);
          if (select) renderFilterOptions(global.document, select, facets[name]);
        });
        addText(optionsStatus, '');
      } catch (_error) {
        addText(optionsStatus, "Some filter options couldn't be loaded. Your current filters are still available. ");
        var retry = global.document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--secondary';
        addText(retry, 'Retry filter options');
        retry.addEventListener('click', loadFilterOptions);
        optionsStatus.appendChild(retry);
      } finally {
        optionsLoading = false;
      }
    }
    if (optionsStatus) loadFilterOptions();

    function renderPayload(payload) {
      renderPagination(global.document, pagination, payload, currentState, function requestPage(page) {
        apply({ page: page, focusResults: true });
      });
      renderBuildCards(global.document, results, payload.builds);
      addText(status, payload.totalItems + (payload.totalItems === 1 ? ' build' : ' builds'));
      results.setAttribute('aria-label', payload.totalPages > 0
        ? 'Build results, page ' + payload.page + ' of ' + payload.totalPages : 'Build results');
      if (focusResultsOnLoad) {
        focusResultsOnLoad = false;
        var header = global.document.querySelector('.terraino-header--persistent');
        results.style.scrollMarginTop = ((header ? header.getBoundingClientRect().height : 0) + 16) + 'px';
        results.focus({ preventScroll: true });
        results.scrollIntoView({ block: 'start' });
      }
    }

    var loader = createLoader({
      fetchImpl: global.fetch.bind(global),
      onSuccess: renderPayload,
      onError: function onError() {
        addText(status, "We couldn't refresh the Builds Compendium. The builds already on this page are still available.");
        var retry = global.document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--secondary';
        addText(retry, 'Retry');
        retry.addEventListener('click', function retryRequest() { loader.load(currentState); });
        status.appendChild(global.document.createTextNode(' '));
        status.appendChild(retry);
      },
    });

    var protectedLoader = global.TerrainoProtectedData ? createLoader({
      fetchImpl: global.fetch.bind(global),
      urlForState: function protectedUrl(state) {
        return buildProtectedPieceRequestUrl(
          state,
          global.TerrainoProtectedData,
          global.crypto,
          global.location.origin,
        );
      },
      onSuccess: renderPayload,
      onError: function onProtectedError() {
        addText(status, "We couldn't apply the protected Piece filter. Clear it to return to public Builds.");
      },
    }) : null;

    function showRequestedPieceFailure() {
      status.replaceChildren();
      if (pieceFacetLoading) {
        addText(status, 'Loading the requested Piece filter…');
        return;
      }
      addText(status, "We couldn't load the requested Piece filter. Retry, or clear it to return to public Builds.");
      if (pieceFacet) {
        var retry = global.document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--secondary';
        addText(retry, 'Retry Piece filter');
        retry.addEventListener('click', loadInitialPieceFacet);
        status.appendChild(retry);
      }
      var clear = global.document.createElement('button');
      clear.type = 'button';
      clear.className = 'button button--secondary';
      addText(clear, 'Clear Piece filter');
      clear.addEventListener('click', function clearRequestedPiece() { apply({ clearPiece: true }); });
      status.appendChild(clear);
    }

    function apply(options) {
      if (root.isConnected === false) return;
      var pieceControl = form.elements.namedItem('piece');
      if (waitingForRequestedPiece) {
        if ((options && (options.clearPiece || options.changePiece)) || (pieceControl && pieceControl.value === requestedPiece)) {
          waitingForRequestedPiece = false;
        } else {
          showRequestedPieceFailure();
          return;
        }
      }
      currentState = stateFromForm(form);
      currentState.page = options && options.page ? options.page : 1;
      focusResultsOnLoad = Boolean(options && options.focusResults);
      if (pieceFacetLoading && options && (options.clearPiece || options.changePiece)) pieceFacetGeneration += 1;
      if (options && options.clearPiece && pieceControl) {
        pieceControl.value = '';
        var pieceDescription = form.querySelector('[data-terraino-piece-selection]');
        if (pieceDescription) addText(pieceDescription, '');
      }
      currentPiece = pieceControl ? String(pieceControl.value || '') : '';
      currentState.piece = currentPiece;
      global.history.replaceState({}, '', global.location.pathname + toAuthorizedHistoryQuery(currentState));
      addText(status, 'Loading builds…');
      if (currentPiece && protectedLoader) {
        loader.abort();
        protectedLoader.load(currentState);
      } else {
        if (protectedLoader) protectedLoader.abort();
        loader.load(currentState);
      }
    }

    form.addEventListener('submit', function onSubmit(event) {
      event.preventDefault();
      apply({ resetPage: true });
    });
    form.addEventListener('change', function onChange(event) {
      if (event.target && event.target.name !== 'query') apply({ resetPage: true, changePiece: event.target.name === 'piece' });
    });
    var queryInput = form.elements.namedItem('query');
    if (queryInput) queryInput.addEventListener('input', debounce(function onQuery() { apply({ resetPage: true }); }, 250));
    var pieceFacet = root.querySelector('[data-terraino-build-piece-facet]');
    function loadInitialPieceFacet() {
      if (pieceFacetLoading) return;
      pieceFacetLoading = true;
      var generation = ++pieceFacetGeneration;
      status.replaceChildren();
      addText(status, 'Loading Piece filters…');
      initializePieceFacet(pieceFacet, form, function isCurrentFacet() { return generation === pieceFacetGeneration; }).then(function applyRequestedPiece(select) {
        pieceFacetLoading = false;
        if (generation !== pieceFacetGeneration || currentState !== initial) return;
        if (waitingForRequestedPiece && (!select || select.value !== requestedPiece)) {
          showRequestedPieceFailure();
        } else if ((select && select.value) || ALLOWED_KEYS.some(function hasInitialKey(key) { return initialParams.has(key); })) {
          apply({ page: initial.page });
        } else {
          addText(status, '');
        }
      });
    }
    if (pieceFacet) loadInitialPieceFacet();
    if (!waitingForRequestedPiece) apply({ page: initial.page });
    else if (!pieceFacet) showRequestedPieceFailure();
  }

  var api = {
    normalizeState: normalizeState,
    buildRequestUrl: buildRequestUrl,
    toHistoryQuery: toHistoryQuery,
    createLoader: createLoader,
    renderBuildCards: renderBuildCards,
    renderPagination: renderPagination,
    fetchBuildFilterOptions: fetchBuildFilterOptions,
    ensureFilterOption: ensureFilterOption,
    renderFilterOptions: renderFilterOptions,
    normalizeHeroImage: normalizeHeroImage,
    buildProtectedPieceRequestUrl: buildProtectedPieceRequestUrl,
    fetchAllPieceFacetPages: fetchAllPieceFacetPages,
    buildPieceFacetOptions: buildPieceFacetOptions,
    initializePieceFacet: initializePieceFacet,
    initialize: initialize,
  };
  global.TerrainoBuildsCompendium = api;

  if (global.document) {
    global.document.querySelectorAll('[data-terraino-builds-compendium]').forEach(initialize);
    global.document.addEventListener('shopify:section:load', function initializeEditorSection(event) {
      var scope = event.target;
      if (scope.matches && scope.matches('[data-terraino-builds-compendium]')) initialize(scope);
      scope.querySelectorAll('[data-terraino-builds-compendium]').forEach(initialize);
    });
  }
})(globalThis);
